import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Search,
  CheckCircle2,
  RefreshCw,
  Clock,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { AdminPayoutItem } from '../../../types/admin.types';
import {
  fetchAdminPayouts,
  approveAdminPayoutApi,
} from '../../../lib/adminService';

interface PayoutsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export const PayoutsTab: React.FC<PayoutsTabProps> = ({ onShowNotice }) => {
  const [payouts, setPayouts] = useState<AdminPayoutItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const loadPayouts = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminPayouts();
      setPayouts(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load payout queue', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPayouts();
  }, []);

  const handleApprovePayout = async (payout: AdminPayoutItem) => {
    setApprovingId(payout.id);
    try {
      await approveAdminPayoutApi(payout.id);
      onShowNotice(`Net payout of ₹${payout.netPayout.toLocaleString('en-IN')} approved for ${payout.companionName}.`, 'success');
      loadPayouts();
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to approve payout', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const totalNetEligible = payouts
    .filter((p) => p.payoutStatus === 'ELIGIBLE' || p.payoutStatus === 'PROCESSING')
    .reduce((a, b) => a + b.netPayout, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Companion Payout Management</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono text-xs font-bold">
              ₹{totalNetEligible.toLocaleString('en-IN')} Queued
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 20 & 21: Payout ledger with 15% platform fee deduction and automated OTP eligibility guard.
          </p>
        </div>

        <button
          type="button"
          onClick={loadPayouts}
          className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer self-start sm:self-auto"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
        </button>
      </div>

      {/* Formula & Rule Card (Section 21 & 49) */}
      <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        <div className="space-y-1">
          <span className="text-slate-400 font-bold block">15% Platform Fee Calculation Rule:</span>
          <div className="font-mono text-purple-300 bg-slate-950 p-2.5 rounded-xl border border-slate-800">
            platform_fee = gross_amount × 15%<br />
            net_payout = gross_amount - platform_fee
          </div>
        </div>
        <div className="space-y-1">
          <span className="text-slate-400 font-bold block">Eligibility Invariant Check:</span>
          <div className="font-mono text-emerald-300 bg-slate-950 p-2.5 rounded-xl border border-slate-800 text-[11px]">
            Payment SUCCESS + Meeting Completed + OTP Verified + No Dispute = ELIGIBLE
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Payout ID</th>
                <th className="py-3 px-4">Booking ID</th>
                <th className="py-3 px-4">Companion</th>
                <th className="py-3 px-4">Gross (₹)</th>
                <th className="py-3 px-4">Platform Fee (15%)</th>
                <th className="py-3 px-4">Net Payout (85%)</th>
                <th className="py-3 px-4">Payout Status</th>
                <th className="py-3 px-4">Eligibility Time</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-500">
                    No payout records in queue.
                  </td>
                </tr>
              ) : (
                payouts.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-purple-400">{p.id}</td>
                    <td className="py-3.5 px-4 font-mono text-slate-300">{p.bookingId}</td>
                    <td className="py-3.5 px-4 font-bold text-white">
                      <div>{p.companionName}</div>
                      <span className="text-[10px] text-slate-500 font-mono">{p.companionId}</span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-slate-300">
                      ₹{p.grossAmount.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-purple-400">
                      ₹{p.platformFee.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-emerald-400 text-sm">
                      ₹{p.netPayout.toLocaleString('en-IN')}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          p.payoutStatus === 'PAID'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : p.payoutStatus === 'PROCESSING' || p.payoutStatus === 'ELIGIBLE'
                            ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                            : p.payoutStatus === 'BLOCKED'
                            ? 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                            : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        }`}
                      >
                        {p.payoutStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                      {new Date(p.eligibleAt).toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      {p.payoutStatus === 'PROCESSING' || p.payoutStatus === 'ELIGIBLE' ? (
                        <button
                          type="button"
                          disabled={approvingId === p.id}
                          onClick={() => handleApprovePayout(p)}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition cursor-pointer disabled:opacity-50"
                        >
                          {approvingId === p.id ? 'Processing...' : 'Approve & Release'}
                        </button>
                      ) : (
                        <span className="text-slate-500 text-[11px] font-mono">
                          {p.payoutStatus === 'PAID' ? 'Transferred ✓' : 'Awaiting Conditions'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
